import {Injectable} from '@angular/core';
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {Store, select} from '@ngrx/store';
import {Observable} from "rxjs";
import {filter, take} from "rxjs/operators";
import {WebMessagesUpdate} from "@ispm/core/store/webMessages.actions";
import {selectWebMessagesStatus} from "@ispm/core/store/webMessages.selector";
import {NotificationMessageService} from "@ispm/core/components/customise-notification/notifications-message.service";

@Injectable()
export class WebMessagingService {
  private webMessagesConfiguration: string;
  private isTestingEnvironment = false;
  private EMRIntegrationParam = 'EMRIntegration';
  private EMRIntegrationParamHyperdrive = 'Hyperdrive';
  private hasHyperdriveURLParam: boolean = false;
  private initiateHandshakeMessage: string = "InitiateHandshake";
  private initiateHandshakeToken: string = undefined;
  private InitiateHandshakeRetry: number;
  private selectProtocolFromPathwaysMessage: string = "SelectProtocolFromPathways";
  private selectProtocolFromPathwaysArgs: object;
  private protocolId: string = undefined;
  private failureMessage: string;
  private timeout: number;
  private currentAction: string;
  private currentMessageName: string;
  private sendMessageLock: boolean = false;
  private keepNotifications: boolean = false;
  private infoNotificationTime: number = 7000;

  constructor(private store: Store<IgenomicsState>,
              public notificationMessageService: NotificationMessageService) {
    this.checkWebMessagesConditions();
  }

  private checkWebMessagesConditions() {
    Observable.of(this.store.select('users', 'organizationConfiguration').pipe(filter(organizationConfiguration => !!organizationConfiguration), take(1))
      .subscribe((orgConfig) => {
        if (!!orgConfig.webMessagesConfiguration) {
          this.webMessagesConfiguration = orgConfig.webMessagesConfiguration;
          this.isTestingEnvironment = this.webMessagesConfiguration["isTesting"];
          this.failureMessage = this.webMessagesConfiguration["failureMessage"];
          this.InitiateHandshakeRetry = this.webMessagesConfiguration["InitiateHandshakeRetry"];
          this.timeout = this.webMessagesConfiguration["timeout"];
          this.selectProtocolFromPathwaysArgs = this.webMessagesConfiguration["actions"][this.selectProtocolFromPathwaysMessage]["args"];
          if (!!this.webMessagesConfiguration["actions"][this.initiateHandshakeMessage]) {
            const params = new URL(location.href).searchParams;
            const EMRIntegrationParam = params.get(this.EMRIntegrationParam);
            if (EMRIntegrationParam == this.EMRIntegrationParamHyperdrive) {
              this.hasHyperdriveURLParam = true;
              this.startService();
            }
          }
        }
      }));
  }

  private startService() {
    window.addEventListener("message", this.iframeWebMessagesListener.bind(this), false);
    this.postMessageByConfig(this.initiateHandshakeMessage);
  }

  public isAvailable(): Observable<string> {
    return this.store.pipe(select(selectWebMessagesStatus));
  }

  public isHyperDriveIntegration() {
    return this.hasHyperdriveURLParam;
  }

  public getSelectProtocolFromPathwaysArgs() {
    return this.selectProtocolFromPathwaysArgs;
  }

  public postMessageByConfig(messageName: string, params: any = null) {
    this.currentAction = this.webMessagesConfiguration["actions"][messageName];
    this.currentMessageName = messageName;
    if (!!this.currentAction && this.hasHyperdriveURLParam) {
      if (!this.sendMessageLock) {
        if (this.currentAction["token"]) {
          this.store.pipe(select(selectWebMessagesStatus)).subscribe((token) => {
            this.saveProtocolID(params);
            if (token) {
              this.currentAction["token"] = token;
              if (this.currentAction["args"]) {
                if (params) {
                  this.currentAction["args"] = params;
                  this.postMessage(messageName, this.currentAction);
                } else {
                  this.showErrorMessage(this.currentMessageName, "The parameters for this action are missing");
                }
              } else {
                this.postMessage(messageName, this.currentAction);
              }
            } else {
              this.showErrorMessage(this.currentMessageName, "The token for this action is missing");
            }
          })
        } else {
          this.postMessage(messageName, this.currentAction);
        }
      } else {
        this.notificationMessageService.info("EMR Integration: another action is in process, please wait for response", null, null, true);
      }
    }
  }

  private saveProtocolID(params: any = null) {
    if (this.currentAction["args"]) {
      if (params) {
        this.currentAction["args"] = params;
        if (!!this.currentAction["args"]["protocolId"]) {
          this.protocolId = this.currentAction["args"]["protocolId"];
        }
      }
    }
  }

  private postMessage(messageName: string, action: any) {
    window.parent?.postMessage(action, "*");
    this.sendMessageLock = true;
    setTimeout(() => {
      if (this.sendMessageLock) {
        this.sendMessageLock = false;
        this.showErrorMessage(this.currentMessageName, "No response until timeout");
      }
    }, this.timeout);
    if (this.isTestingEnvironment) {
      this.notificationMessageService.info("Post " + messageName + "message", this.infoNotificationTime, null, true);
    }
  }

  private errorHandling(errorMessage: string, errorCode: string) {
    if (this.currentMessageName == this.initiateHandshakeMessage) {
      if (this.InitiateHandshakeRetry > 0) {
        if (this.isTestingEnvironment) {
          this.notificationMessageService.info("Retry action: " + this.currentMessageName + "retry number: " + this.InitiateHandshakeRetry, this.infoNotificationTime, null, true);
        }
        this.InitiateHandshakeRetry--;
        this.postMessageByConfig(this.currentMessageName);
      } else {
        this.showErrorMessage(this.currentMessageName, errorMessage);
      }
    } else {
      if (!!errorCode) {
        let errorMessageActionConfig = this.webMessagesConfiguration["errorMessageAction"][errorCode];
        if (!!errorMessageActionConfig) {
          if (!!errorMessageActionConfig["retry"]) {
            if (errorMessageActionConfig["retry"] > 0) {
              if (this.isTestingEnvironment) {
                this.notificationMessageService.info("retry action: " + this.currentMessageName + "retry number: " + this.webMessagesConfiguration["errorMessageAction"][errorCode]["retry"], this.infoNotificationTime, null, true);
              }
              this.webMessagesConfiguration["errorMessageAction"][errorCode]["retry"]--;
              this.postMessageByConfig(this.currentMessageName);
            } else {
              this.showErrorMessage(this.currentMessageName, errorMessage);
            }
          }
        } else {
          this.showErrorMessage(this.currentMessageName, errorMessage);
        }
      } else {
        this.showErrorMessage(this.currentMessageName, errorMessage);
      }
    }
  }

  private showErrorMessage(actionMessage: string, error: string) {
    if (this.isTestingEnvironment) {
      this.keepNotifications = true;
    }
    if (!!this.protocolId && actionMessage == this.selectProtocolFromPathwaysMessage) {
      this.notificationMessageService.error(this.failureMessage[actionMessage] + " " + this.protocolId + " to Beacon.<br/>Error: " + error, null, null, this.keepNotifications);
    } else {
      this.notificationMessageService.error(this.failureMessage[actionMessage] + ".<br/>Error: " + error, null, null, this.keepNotifications);
    }
  }

  private iframeWebMessagesListener(event) {
    if (event.data["actionExecuted"] !== undefined) {
      this.sendMessageLock = false;
      if (!event.data["actionExecuted"]) {
        if (!!event.data["errorCodes"] && event.data["errorCodes"].length > 0) {
          let errorMessage = event.data["error"] ? event.data["error"] : "No error message was sent";
          for (let errorCode of event.data["errorCodes"]) {
            this.errorHandling(errorMessage, errorCode);
          }
        } else {
          this.showErrorMessage(this.currentMessageName, "ActionExecuted: false");
        }
      } else {
        if (!!event.data["token"]) {
          this.initiateHandshakeToken = event.data["token"];
          this.store.dispatch(new WebMessagesUpdate({value: this.initiateHandshakeToken}));
          if (this.isTestingEnvironment) {
            this.notificationMessageService.info("Got initiate handshake token " + this.initiateHandshakeToken, this.infoNotificationTime, null, true);
          }
        } else {
          if (this.isTestingEnvironment) {
            this.notificationMessageService.info("Got: " + JSON.stringify(event.data), this.infoNotificationTime, null, true);
          }
        }
      }
    }
  }

  public stopService() {
    window.removeEventListener("message", this.iframeWebMessagesListener);
  }
}
